import type {
  BrokerOrg,
  BrokerOrgInvite,
  BrokerOrgMember,
  BrokerOrgMemberRole,
  BrokerOrgStatus
} from '@types';

export type ApiBrokerOrganisation = {
  organisationId: string;
  name?: string;
  abn?: string;
  emailDomains?: string[] | null;
  autoJoin?: boolean | null;
  status?: string | null;
};

export type ApiBrokerUser = {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[] | null;
  userStatus?: string | null;
  status?: string | null;
  lastLogin?: string | null;
  createdTime?: string | null;
};

const PENDING_STATUSES = ['INVITATION_PENDING', 'INVITATION_EXPIRED'];

export const toApiRole = (role: BrokerOrgMemberRole): 'ADMIN' | 'STANDARD' =>
  role === 'ADMIN' ? 'ADMIN' : 'STANDARD';

const memberRole = (roles?: string[] | null): BrokerOrgMemberRole =>
  roles?.includes('ADMIN') ? 'ADMIN' : 'MEMBER';

const userStatus = (user: ApiBrokerUser) => user.userStatus || user.status || '';

const memberStatus = (status: string): BrokerOrgMember['status'] =>
  status === 'ACTIVE' ? 'ACTIVE' : 'PENDING';

export const isPendingInvite = (user: ApiBrokerUser) =>
  PENDING_STATUSES.includes(userStatus(user));

export const toBrokerOrgMember = (user: ApiBrokerUser): BrokerOrgMember => ({
  userId: user.userId,
  email: user.email ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
  role: memberRole(user.roles),
  status: memberStatus(userStatus(user)),
  lastLogin: user.lastLogin ?? null
});

export const toBrokerOrgInvite = (user: ApiBrokerUser): BrokerOrgInvite => ({
  inviteId: user.userId,
  email: user.email ?? '',
  invitedAt: user.createdTime ?? '',
  status: userStatus(user) === 'INVITATION_EXPIRED' ? 'EXPIRED' : 'PENDING'
});

const orgStatus = (status?: string | null): BrokerOrgStatus =>
  status === 'ACTIVE' ? 'ACTIVE' : 'PENDING';

export const mapBrokerOrg = (
  organisation: ApiBrokerOrganisation,
  users: ApiBrokerUser[] = []
): BrokerOrg => ({
  brokerOrgId: organisation.organisationId,
  name: organisation.name ?? '',
  emailDomain: organisation.emailDomains?.[0] ?? '',
  abn: organisation.abn ?? '',
  status: orgStatus(organisation.status),
  autoJoin: organisation.autoJoin ?? false,
  existingDomainUserCount: 0,
  members: users.map(toBrokerOrgMember),
  pendingInvites: users.filter(isPendingInvite).map(toBrokerOrgInvite)
});
